import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Briefcase } from 'lucide-react';
import { usePhoneLinks } from '../utils/phoneUtils';
import LanguageSwitcher from './LanguageSwitcher';

interface NavigationProps {
  scrollToSection: (id: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({ scrollToSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const phoneLinks = usePhoneLinks('4244669163');

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    if (!isHomePage) {
      window.location.href = `/#${sectionId}`;
    } else {
      scrollToSection(sectionId);
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className="bg-white/90 backdrop-blur-sm border-b border-slate-200/50 sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <span className="text-xl sm:text-2xl font-bold text-gray-900">grokly</span>
              <span className="text-xl sm:text-2xl font-semibold ml-1">
                <span className="text-[#4361ee]">Medical</span>
                <span className="text-gray-900">Media</span>
              </span>
            </Link>
          </div>

          <div className="hidden lg:flex items-center space-x-8">
            <a href={phoneLinks.href} className="flex items-center text-gray-600 hover:text-blue-600 transition-colors">
              <Phone size={16} className="mr-2" />
              {phoneLinks.display}
            </a>
            <a href="mailto:info@groklymed.me" className="flex items-center text-gray-600 hover:text-blue-600 transition-colors">
              <Mail size={16} className="mr-2" />
              info@groklymed.me
            </a>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className="relative text-gray-800 hover:text-blue-600 font-medium transition-colors duration-200 group"
            >
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-200"></span>
            </Link>
            <a 
              href="#services" 
              onClick={(e) => handleNavClick(e, 'services')}
              className="relative text-gray-800 hover:text-blue-600 font-medium transition-colors duration-200 group"
            >
              Services
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-200"></span>
            </a>
            <Link 
              to="/resources" 
              className="relative text-gray-800 hover:text-blue-600 font-medium transition-colors duration-200 group"
            >
              Resources
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-200"></span>
            </Link>
            <Link 
              to="/blog" 
              className="relative text-gray-800 hover:text-blue-600 font-medium transition-colors duration-200 group"
            >
              Blog
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-200"></span>
            </Link>
            <Link 
              to="/portfolio" 
              className="relative text-gray-800 hover:text-blue-600 font-medium transition-colors duration-200 group"
            >
              Portfolio
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-200"></span>
            </Link>

            <LanguageSwitcher />

            <a
              href="#contact"
              onClick={(e) => handleNavClick(e, 'contact')}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 transform hover:scale-105 hover:shadow-lg"
            >
              Contact Us
            </a>
          </div>

          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-gray-800 hover:text-blue-600">Home</Link>
              <a href="#services" onClick={(e) => handleNavClick(e, 'services')} className="text-gray-800 hover:text-blue-600">Services</a>
              <Link to="/resources" className="text-gray-800 hover:text-blue-600">Resources</Link>
              <Link to="/blog" className="text-gray-800 hover:text-blue-600">Blog</Link>
              <Link to="/portfolio" className="text-gray-800 hover:text-blue-600">Portfolio</Link>
              <a href="#contact" onClick={(e) => handleNavClick(e, 'contact')} className="text-gray-800 hover:text-blue-600">Contact Us</a>
              <div className="pt-2">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;